// mirror of auware/.../frontend/src/components/ui/Input.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type InputHTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label displayed above the input. */
  label?: string;
  /** Error message displayed below the input. */
  error?: string;
  /** Helper text displayed below the input (hidden when `error` is set). */
  helperText?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseInputClasses =
  "mt-1 block w-full rounded-lg border-neutral-300 shadow-sm " +
  "focus:border-primary-500 focus:ring-primary-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

const errorInputClasses =
  "mt-1 block w-full rounded-lg border-red-500 shadow-sm " +
  "focus:border-red-500 focus:ring-red-500 sm:text-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Text input with optional label, error state, and helper text.
 *
 * ```tsx
 * <Input label="Email" placeholder="you@example.com" error={errors.email} />
 * ```
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className = "", label, error, helperText, id, ...props }, ref) => {
    return (
      <div>
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-medium text-neutral-700"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          className={`${error ? errorInputClasses : baseInputClasses} ${className}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? `${id}-error` : helperText ? `${id}-helper` : undefined
          }
          {...props}
        />
        {error && (
          <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${id}-helper`} className="mt-1 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
